import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconChevronDown, IconSearch, IconX } from '@tabler/icons-react';
import { QUICK_FONT_FAMILIES } from '../constants';
import {
    FONT_CATALOG,
    FONT_CATEGORIES,
    FONT_SCRIPTS,
    ensureFontPreview,
    loadFontFamily,
    previewFamily,
} from '../engine/fonts';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { Popover, refocusTextEditor } from '../ui/prims';
import Hint from '../../../components/common/Hint';

/**
 * Font family picker — the same catalogue the print editor offers (~1,900
 * Google families plus the local system faces).
 *
 * Two things a list that long cannot do without: the rows are WINDOWED (mounting
 * two thousand nodes janks the popover open) and each row's preview face is
 * fetched only when it scrolls into view — a name-sized subset under an aliased
 * family, so browsing the list costs a few KB rather than the whole library.
 */

const ROW_H = 30;
const OVERSCAN = 6;
const LIST_H = 264;
const PANEL_W = 268;

/** One row, set in its own face once that face has arrived. */
function FontRow({ family, selected, onPick }) {
    const [ready, setReady] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const node = ref.current;
        if (!node) return undefined;
        let alive = true;
        const io = new IntersectionObserver((entries) => {
            if (!entries.some((e) => e.isIntersecting)) return;
            io.disconnect();
            ensureFontPreview(family).then((ok) => { if (alive && ok) setReady(true); });
        }, { rootMargin: '120px' });
        io.observe(node);
        return () => { alive = false; io.disconnect(); };
    }, [family]);

    return (
        <button
            ref={ref}
            type="button"
            onClick={() => onPick(family)}
            // No Hint here: the row already says the name, and a pill chasing
            // the pointer down a list of two thousand is noise. `title` is kept
            // for the ones the row is too narrow to show whole.
            title={family}
            style={{
                height: ROW_H,
                // Until the preview lands the row stays in the UI font, never blank.
                fontFamily: ready
                    ? `"${previewFamily(family)}", "${family}", sans-serif`
                    : `"${family}", sans-serif`,
            }}
            className={`flex w-full items-center truncate rounded-md px-2 text-left text-[13px] transition-colors ${
                selected
                    ? 'bg-primary/10 text-primary'
                    : 'text-[#3D3833] hover:bg-[#F4F1ED] dark:text-zinc-200 dark:hover:bg-zinc-800'
            }`}
        >
            {family}
        </button>
    );
}

/**
 * @param {{ value: string, onChange: (family: string) => void,
 *   bold?: boolean, italic?: boolean, label?: string }} props
 *   `label` overrides the button text — "Edit text" boxes are set in the
 *   document's own embedded face, which is in no catalogue.
 */
export default function FontPicker({ value, onChange, bold, italic, label }) {
    const { t } = useTranslation();
    const anchorRef = useRef(null);
    const listRef = useRef(null);
    const searchRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [filter, setFilter] = useState('all');
    const [scrollTop, setScrollTop] = useState(0);

    const items = useMemo(() => {
        const q = query.trim().toLowerCase();
        const rows = FONT_CATALOG.filter((f) => {
            if (q && !f.name.toLowerCase().includes(q)) return false;
            if (filter === 'all') return true;
            if (filter.startsWith('cat:')) return f.category === filter.slice(4);
            if (filter.startsWith('scr:')) {
                const i = FONT_SCRIPTS.findIndex(([key]) => key === filter.slice(4));
                return i >= 0 && (f.scriptMask & (1 << i)) !== 0;
            }
            return true;
        }).map((f) => f.name);

        // The families this editor reaches for constantly sit on top, so the
        // common case never needs a search.
        if (filter !== 'all') return rows;
        const quick = QUICK_FONT_FAMILIES.filter((name) => rows.includes(name));
        return [...quick, ...rows.filter((name) => !quick.includes(name))];
    }, [query, filter]);

    useEffect(() => {
        if (!open) return;
        setQuery('');
        setScrollTop(0);
        if (listRef.current) listRef.current.scrollTop = 0;
    }, [open]);

    // The panel is portalled and only mounts once it has been positioned, so
    // the search box takes focus the moment it exists — waiting a frame loses
    // whatever was typed in between.
    const focusSearch = useCallback((node) => {
        searchRef.current = node;
        node?.focus();
    }, []);

    const pick = useCallback((family) => {
        onChange(family);
        // Fetch the real face straight away, so the canvas does not paint a
        // fallback in the gap between the click and the font arriving.
        loadFontFamily(family, bold, italic);
        setOpen(false);
        // The search box took the caret; give it back to the text being edited.
        refocusTextEditor();
    }, [onChange, bold, italic]);

    const first = Math.max(0, Math.floor(scrollTop / ROW_H) - OVERSCAN);
    const last = Math.min(items.length, Math.ceil((scrollTop + LIST_H) / ROW_H) + OVERSCAN);

    return (
        <>
            <Hint label={label || value}>
                <button
                    ref={anchorRef}
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    aria-label={label || value}
                    className={`relative flex h-8 w-full items-center rounded-lg border bg-white px-2 pr-7 text-left text-[12.5px] text-[#1C1815] outline-none transition-colors dark:bg-zinc-900 dark:text-zinc-100 ${
                        open ? 'border-primary' : 'border-[#ECE8E4] dark:border-zinc-700'
                    }`}
                >
                    <span className="truncate">{label || value}</span>
                    <IconChevronDown
                        size={13}
                        stroke={2}
                        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[#A8A099]"
                    />
                </button>
            </Hint>

            <Popover
                anchorRef={anchorRef}
                open={open}
                onClose={() => { setOpen(false); refocusTextEditor(); }}
                width={PANEL_W}
            >
                <div className="relative mb-2">
                    <IconSearch size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#A8A099]" />
                    <input
                        ref={focusSearch}
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setScrollTop(0); if (listRef.current) listRef.current.scrollTop = 0; }}
                        onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === 'Enter' && items[0]) pick(items[0]);
                        }}
                        placeholder={t('pdfEditor.fonts.search')}
                        className="h-8 w-full rounded-lg border border-[#ECE8E4] bg-white pl-7 pr-7 text-[12.5px] outline-none focus:border-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                    />
                    {query ? (
                        <button
                            type="button"
                            onClick={() => { setQuery(''); searchRef.current?.focus(); }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-[#A8A099] hover:text-[#1C1815] dark:hover:text-zinc-100"
                        >
                            <IconX size={13} />
                        </button>
                    ) : null}
                </div>

                <select
                    value={filter}
                    onChange={(e) => { setFilter(e.target.value); setScrollTop(0); if (listRef.current) listRef.current.scrollTop = 0; }}
                    className="mb-2 h-8 w-full rounded-lg border border-[#ECE8E4] bg-white px-2 text-[12.5px] text-[#1C1815] outline-none focus:border-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                >
                    <option value="all">{t('pdfEditor.fonts.all')}</option>
                    <optgroup label={t('pdfEditor.fonts.category')}>
                        {FONT_CATEGORIES.map((c) => <option key={c} value={`cat:${c}`}>{c}</option>)}
                    </optgroup>
                    <optgroup label={t('pdfEditor.fonts.script')}>
                        {FONT_SCRIPTS.map(([key, name]) => (
                            <option key={key} value={`scr:${key}`}>{name}</option>
                        ))}
                    </optgroup>
                </select>

                <div
                    ref={listRef}
                    onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
                    className="overflow-y-auto"
                    style={{ height: LIST_H }}
                >
                    {items.length === 0 ? (
                        <div className="flex h-full items-center justify-center text-[12px] text-[#A8A099]">
                            {t('pdfEditor.fonts.empty')}
                        </div>
                    ) : (
                        // A spacer of the full height keeps the scrollbar honest
                        // while only the visible slice is mounted.
                        <div style={{ height: items.length * ROW_H, position: 'relative' }}>
                            <div style={{ transform: `translateY(${first * ROW_H}px)` }}>
                                {items.slice(first, last).map((family) => (
                                    <FontRow
                                        key={family}
                                        family={family}
                                        selected={family === value}
                                        onPick={pick}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </Popover>
        </>
    );
}
